<template>
  <v-layout>
    <v-navigation-drawer class="navigation-drawer" absolute width="500" :src="leftNavBarImageUrl"></v-navigation-drawer>
    <div class="main-content d-flex flex-column justify-content-center align-items-center">
      <Loader v-if="isPageLoading"/>
      <div v-else-if="!passwordSet && !isError" class="d-flex flex-column set-password-container" >
        <v-img class="mb-12" v-if="user.orgLogoUrl" :src="user?.orgLogoUrl" max-height="120" max-width="100" contain></v-img>

        <p class="form-title">Create Password</p>

        <p class="form-hint pb-4">
          Enter the password you would like to use to sign in to FamilyHub
        </p>
        <v-form ref="form" lazy-validation @submit.prevent="setPassword" v-model="isFormValid">
          <v-text-field
            name="password"
            v-model="password"
            label="Create Password"
            :rules="rules.password"
            required
            :append-icon="isVisiblePassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="isVisiblePassword ? 'text' : 'password'"
            @click:append="isVisiblePassword = !isVisiblePassword"
          >
          </v-text-field>
          <v-text-field
            name="repeatPassword"
            v-model="repeatPassword"
            label="Confirm Password"
            class="pt-4"
            :rules="rules.repeatPassword"
            required
            :append-icon="isVisibleRepeatPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="isVisibleRepeatPassword ? 'text' : 'password'"
            @click:append="isVisibleRepeatPassword = !isVisibleRepeatPassword"
          ></v-text-field>
          <v-btn
            type="submit"
            color="primary"
            block
            class="mt-8"
            :disabled="!isFormValid || isLoading"
            :loading="isLoading"
          >SEND
          </v-btn>
        </v-form>
      </div>
      <div v-if="passwordSet" class="justify-content-center align-items-center pt-8">
        <div>
          <h3 class="font-weight-bold">Password successfully set</h3>
        </div>
        <div>
          <GetApps :linkToFamilyHubForAndroid="user.linkToFamilyHubForAndroid" :linkToFamilyHubForIos="user.linkToFamilyHubForIos"></GetApps>
        </div>
      </div>
      <div class="d-flex pt-8" v-if="isError">
        <h4 align="center" class="font-weight-bold">{{ errorMessage }}</h4>
      </div>
    </div>
  </v-layout>
</template>

<script>
import * as firebase from "firebase/app";
import {ValidationRules} from "@/utils/validation-rules"
import {getRegionName} from "@/utils/functionsRegion";
import GetApps from "@/components/GetApps.vue";
import Loader from "@/components/loader/Loader.vue";

export default {
  name: 'SetFamilyHubUserPassword',
  components: {
    GetApps, 
    Loader
  },
  data() {
    return {
      leftNavBarImageUrl: require("../../../assets/left-nav-bar/left-nav-bar-1.svg"),
      user: {id: null, 
        orgLogoUrl: null,
        linkToFamilyHubForAndroid: null,
        linkToFamilyHubForIos: null
       },
      password: null,
      isVisiblePassword: false,
      isVisibleRepeatPassword: false,
      isFormValid: true,
      isLoading: false,
      isPageLoading: true,
      isError: false,
      passwordSet: false,
      repeatPassword: "",
      passwordErrorMessage: 'Unable to set password',
      unhandledErrorMessage: 'Unhandled error',
      errorMessage: ''
    };
  },
  computed: {
    url() {
      return window.location.href;
    },
    rules() {
      return {
        password: [
          ValidationRules.required,
          ValidationRules.min(6),
          ValidationRules.number,
          ValidationRules.capitalLetter,
          ValidationRules.lowerLetter,
          ValidationRules.symbol,
        ],
        repeatPassword: [
          ValidationRules.required,
          ValidationRules.min(6),
          ValidationRules.isEqualTo(this.password),
        ],
      };
    },
  },

  async created() {
    await firebase.app().functions(getRegionName()).httpsCallable('familyUser-getByToken')({
      setPasswordToken: this.$route.params.token,
    })
    .then((result) => {
      if (result?.data?.status === 200) {
        this.user.id = result.data.data.id
        this.user.orgLogoUrl = result.data.data.orgLogoUrl
        this.user.linkToFamilyHubForAndroid = result.data.data.linkToFamilyHubForAndroid
        this.user.linkToFamilyHubForIos = result.data.data.linkToFamilyHubForIos
        this.isPageLoading = false
      } else{
        this.isPageLoading = false
        this.setError(true, result?.data?.message || 'Unable to set password')
      }
    })
  },

  methods: {
    setError(state, message) {
      if (state) {
        this.isError = true;
        this.errorMessage = message;
      } else {
        this.isError = false;
        this.errorMessage = '';
      }
    },
    setPassword() {
      this.isFormValid = this.$refs.form.validate();

      if (!this.isFormValid) return;

      this.isLoading = true;

      firebase
        .app()
        .functions(getRegionName())
        .httpsCallable('familyUser-createFamilyAuthUser')({
          familyMemberId: this.user.id,
          password: this.password,
        })
        .then(res => {
          if (res.data === true) {
            this.passwordSet = true;
          } else {
            console.error('Error creating auth user');
            this.setError(true, this.unhandledErrorMessage);
          }
        })
        .catch(() => {
          console.error('Error setting password');
          this.setError(true, this.passwordErrorMessage);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.logo-wrapper {
  padding-bottom: 68px;
  padding-left: 16px;
}

.form-title {
  color: #000000de;
  font-size: 20px;
  font-weight: bold;
  line-height: 36px;
}

.form-hint {
  font-size: 16px;
  font-weight: 400;
  line-height: 28px;
  color: #939393;
}

.p-4 {
  padding: 0 !important;
}

.main-content {
  margin-left: 500px; /* width of v-navigation-drawer */
  width: calc(100% - 500px); /* width of the available area */
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.set-password-container {
  margin-top: 100px;
  max-width: 380px;
}

/* for mobile devices */
@media (max-width: 1300px) {
  .navigation-drawer {
    display: none;
  }

  .set-password-container {
    margin-top: 0px;
  }

  .main-content {
    margin-left: 0;
    width: 100%;
    height: 80vh; /* vertical centering */
  }

  .form-title {
    font-size: 20px;
    line-height: 28px;
  }

  .form-hint {
    font-size: 16px;
    line-height: 24px;
  }
}
</style>
